import {
  createSlice,
  Draft,
  PayloadAction
} from "@reduxjs/toolkit";
import {
  Scenario,
  ScenarioTestCase
} from "../../types/scenarioTypes";
import { TransformExecuteResponse } from "../../types/clientTypes";
import { TestCaseTemplate } from "../../types/testCaseTemplateTypes";
import {
  getTemplateAndTestCaseByTestCaseId,
  getTransformTestCase
} from "../../utils/testCaseUtils";
import { SelectedVariable } from "../../types/variableType";

type State = {
  selectedScenario: Scenario,
  selectedTestCases: Array<ScenarioTestCase>,
  editTestCaseId?: number,
  scenarioExecuteResponses: Array<TransformExecuteResponse>
}

const initialState: State = {
  selectedScenario: {
    name: "",
    assignedRequests: [],
    description: "",
    maximumExecutionTimeout: 0
  },
  selectedTestCases: [],
  scenarioExecuteResponses: []
}

const scenarioSlice = createSlice({
  name: "scenarioSlice",
  initialState,
  reducers: {
    setScenario: (state, action: PayloadAction<{ scenario: Scenario, templates: Array<TestCaseTemplate>, selectedVariables: Array<SelectedVariable>}>) => {
      const testCaseTemplates = action.payload.templates;
      const scenario = action.payload.scenario;
      state.selectedScenario = scenario;
      state.selectedTestCases = scenario.assignedRequests.map(request => {
        const testCaseAndTemplate = getTemplateAndTestCaseByTestCaseId(testCaseTemplates, request.requestId);
        if (testCaseAndTemplate) {
          const findTestCase = getTransformTestCase(action.payload.selectedVariables, testCaseAndTemplate?.template, testCaseAndTemplate?.testCase);
          const scenarioTestCase: ScenarioTestCase = {
            ...findTestCase,
            queueNumber: request.queueNumber,
          };
          scenarioTestCase.internalId = request.id?.toString();
          return scenarioTestCase;
        } else {
          return {
            testCaseTemplateId: 0,
            queueNumber: 0,
            responseParameterValues: [],
            requestParameterValues: [],
            name: ""
          };
        }
      });
    },
    setEditTestCaseId: (state, action: PayloadAction<number | undefined>) => {
      state.editTestCaseId = action.payload;
    },
    updateScenarioSelectedTestCase: (state, action: PayloadAction<Array<ScenarioTestCase>>) => {
      state.selectedTestCases = action.payload;
    },
    deleteScenario: (state) => {
      deleteSelectedScenarioLogic(state);
    },
    setScenarioExecuteResponse: (state, action: PayloadAction<Array<TransformExecuteResponse>>) => {
      state.scenarioExecuteResponses = action.payload;
    },
    deleteScenarioExecuteResponse(state) {
      state.scenarioExecuteResponses = [];
    },
    deleteScenarioExecuteResponsesByIds(state, action: PayloadAction<Array<string | undefined>>) {
      const internalIds = action.payload;
      state.scenarioExecuteResponses = state.scenarioExecuteResponses.map(item => {
        if (internalIds.includes(item.internalId)) {
          return {
            statusCode: 0,
            responseBody: "",
            time: 0,
            internalId: item.internalId
          }
        } else {
          return item;
        }
      });
    },
    updateScenarioExecuteResponse: (state, action: PayloadAction<TransformExecuteResponse>) => {
      const executeResponse = action.payload;
      let newExecuteResponse = state.scenarioExecuteResponses.find(item => item.internalId === executeResponse.internalId);
      if (!!newExecuteResponse) {
        newExecuteResponse.responseBody = executeResponse.responseBody;
        newExecuteResponse.isLoad = executeResponse.isLoad || false;
        newExecuteResponse.time = executeResponse.time;
        newExecuteResponse.statusCode = executeResponse.statusCode;
        newExecuteResponse.isError = executeResponse.isError;
        newExecuteResponse.isLoadTestrail = executeResponse.isLoadTestrail;
        newExecuteResponse.testrailResult = executeResponse.testrailResult;
        newExecuteResponse.result = executeResponse.result;
        newExecuteResponse.validationErrorMessage = executeResponse.validationErrorMessage;
      } else {
        state.scenarioExecuteResponses.push(executeResponse);
      }
    },
  }
});

const deleteSelectedScenarioLogic = (state: Draft<State>) => {
  state.selectedScenario = {
    id: 0,
    name: "",
    assignedRequests: [],
    description: "",
    maximumExecutionTimeout: 0
  };
  state.scenarioExecuteResponses = [];
  state.selectedTestCases = [];
}

export const {
  setScenario,
  setEditTestCaseId,
  updateScenarioSelectedTestCase,
  setScenarioExecuteResponse,
  deleteScenario,
  deleteScenarioExecuteResponse,
  deleteScenarioExecuteResponsesByIds,
  updateScenarioExecuteResponse
} = scenarioSlice.actions;

export default scenarioSlice.reducer;